import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/useAuth";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Box,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../config/config";

// AdminBusinessLocation relation
interface AdminBusinessLocation {
  adminId: string;
}

// BusinessLocation inside the store
interface BusinessLocation {
  id: string;
  name: string;
  address: string;
  type: string;
  created_at: string; // ISO date string
  updated_at: string; // ISO date string
  AdminBusinessLocations: AdminBusinessLocation[];
}

// Store object inside the product-location
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Store {
  id: string;
  name: string;
  phone_number: string | null;
  email: string | null;
  opening_hours: any | null; // can replace with a more detailed type if needed
  status: string;
  capacity: number | null;
  latitude: string | null;
  longitude: string | null;
  region: string | null;
  city: string | null;
  store_code: string | null;
  image_url: string | null;
  created_at: string; // ISO date
  updated_at: string; // ISO date
  id_BusinessLocation: BusinessLocation;
}

// Main interface for your product in store
interface Inventory {
  id: string;
  store_id: string | null;
  product_id: string;
  quantity: number;
  reorder_level: number;
  price_override: string;
  expiry_date: string;
  batch_number: string;
  location_in_store: string;
  status: string;
  warehouse_id: string | null;
}

const Inventories: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<Inventory[] | any>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        setLoading(true);
        if (!user) return null;
        const res = await fetch(
          `${API_URL}/inventory/${user.id}/`
        );
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || "Failed to fetch store");
        }
        const json: any = await res.json();
        const inventories = Object.values(json.data);
        setData(inventories);
      } catch (err: any) {
        setError(err.message || "Unexpected error");
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ mt: 4 }}>
        No Inventories found.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column", // stacked vertically but each card is horizontal
        gap: 2,
        mt: 2,
      }}
    >
      {data.map((inv: any) => (
        <Card
          onClick={() => navigate(`/inventory/${inv.id}`)}
          key={inv.id}
          sx={{
            display: "flex", // content side by side
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderRadius: 2,
            boxShadow: 3,
            transition: "0.3s",
            "&:hover": { boxShadow: 6 },
            width: "100%", // take full available width
            maxWidth: "800px", // you can adjust
            mx: "auto", // center horizontally
            backgroundColor: inv.store_id ? "#caf0f8" : "#90e0ef",
            cursor: "pointer",
          }}
        >
          {/* Left side info */}
          <CardContent sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Batch: {inv.batch_number}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Location: {inv.location_in_store}
            </Typography>
            <Typography variant="body1" fontWeight="medium" gutterBottom>
              Price: €{inv.price_override}
            </Typography>
            {inv.store_id ? (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Store: {inv.store?.name ?? "N/A"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {inv.store?.id_BusinessLocation?.address ?? "No address"}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Warehouse: {inv.warehouse?.name ?? "N/A"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {inv.warehouse?.id_BusinessLocation?.address ?? "No address"}
                </Typography>
              </>
            )}
          </CardContent>

          {/* Right side chips */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
              minWidth: "150px",
            }}
          >
            <Chip
              label={`Quantity: ${inv.quantity}`}
              color={inv.quantity <= inv.reorder_level ? "error" : "primary"}
              size="medium"
            />
            <Chip
              label={`Reorder @ ${inv.reorder_level}`}
              variant="outlined"
              size="medium"
            />
          </Box>
        </Card>
      ))}
    </Box>
  );
};
export default Inventories;
